import { useState } from 'react';
import { alpha, Box, IconButton, styled, Tooltip, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { pythonFunctionTestRun } from 'api/workflows/requests';
import { PythonFunctionTestRunResponse } from 'api/model';
import { errorHandler } from 'api/axios_instance';
import { useStore } from 'store/store';
import useDialog from 'hooks/useDialog';
import WorkflowParameterValuesDialog from 'views/flow/WorkflowParameterValuesDialog';
import { FONT_MONO } from 'themes/tokens';

type Props = {
  functionCode?: string;
  extraLibs?: string[];
  inputCount?: number;
  disabled?: boolean;
};

const LogBlock = styled('pre')(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1.25),
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  fontFamily: FONT_MONO,
  fontSize: '0.72rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  overflow: 'auto',
  maxHeight: 192,
  color: theme.palette.text.secondary,
}));

const PythonFunctionTestRun = ({ functionCode, extraLibs, inputCount = 1, disabled }: Props) => {
  const { currentWorkflowParameters } = useStore();

  // @ts-expect-error: incompatible onSubmit args
  const [showTestRunParametersDialog, TestRunParametersDialog] = useDialog(WorkflowParameterValuesDialog);

  const [testRunLoading, setTestRunLoading] = useState(false);
  const [testRunResult, setTestRunResult] = useState<PythonFunctionTestRunResponse>();

  const runTest = async (parameters?: Record<string, string>) => {
    if (!functionCode) {
      toast.error('Function code is required to run a test');
      return;
    }

    setTestRunLoading(true);
    setTestRunResult(undefined);
    try {
      const result = await pythonFunctionTestRun({
        function_code: functionCode,
        input_count: Math.max(inputCount, 1),
        extra_libs: extraLibs,
        parameters,
      });
      setTestRunResult(result);
    } catch (error) {
      errorHandler('Test run failed')(error as Error);
    } finally {
      setTestRunLoading(false);
    }
  };

  const handleTestRunClick = () => {
    if (!functionCode) {
      toast.error('Function code is required to run a test');
      return;
    }
    if (currentWorkflowParameters.length === 0) {
      void runTest();
      return;
    }
    showTestRunParametersDialog(true);
  };

  const handleTestRunWithParameters = (parameters: Record<string, string>) => {
    showTestRunParametersDialog(false);
    void runTest(parameters);
  };

  const tone = testRunResult?.success ? 'success' : 'error';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <Tooltip title="Run the function code with empty inputs to verify it works correctly.">
          <LoadingButton
            loading={testRunLoading}
            loadingPosition="start"
            variant="outlined"
            color="success"
            startIcon={<PlayArrowIcon />}
            onClick={handleTestRunClick}
            disabled={disabled}
          >
            Test Run
          </LoadingButton>
        </Tooltip>
      </div>
      {testRunResult && (
        <Box
          sx={{
            borderRadius: '12px',
            border: (t) => `1px solid ${alpha(t.palette[tone].main, 0.4)}`,
            backgroundColor: (t) => alpha(t.palette[tone].main, 0.06),
            p: 1.75,
          }}
        >
          <div className="flex items-center gap-1.5">
            {testRunResult.success ? (
              <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 20 }} />
            ) : (
              <ErrorOutlineRoundedIcon sx={{ color: 'error.main', fontSize: 20 }} />
            )}
            <Typography sx={{ fontWeight: 700, color: `${tone}.main` }}>
              {testRunResult.success ? 'Test passed' : 'Test failed'}
            </Typography>
            <IconButton
              size="small"
              aria-label="Dismiss result"
              onClick={() => setTestRunResult(undefined)}
              sx={{ ml: 'auto', color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          {testRunResult.error && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.primary' }}>
              {testRunResult.error}
            </Typography>
          )}
          {testRunResult.error_traceback && <LogBlock className="mt-2">{testRunResult.error_traceback}</LogBlock>}
          {testRunResult.logs.length > 0 && (
            <div className="mt-2 flex flex-col gap-1">
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Logs
              </Typography>
              <LogBlock>{testRunResult.logs.join('\n')}</LogBlock>
            </div>
          )}
        </Box>
      )}
      <TestRunParametersDialog
        // @ts-expect-error: incompatible onSubmit args
        onSubmit={handleTestRunWithParameters}
        title="Test Run Parameters"
        confirmText="Run"
        parameters={currentWorkflowParameters}
      />
    </div>
  );
};

export default PythonFunctionTestRun;
